import { Club, ClubPlayer, Group } from "@mixitone/models";
import { batch } from "@mixitone/mvc";
import { List, subscribeList } from "@mixitone/oom";
import { isNil, withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import MixitoneController from "controllers/MixitoneController";
import { generateMidIndex } from "lib/groupIndex";
import { sharedAgentResources } from "lib/sharedAgentResources";
import agentToolsService from "services/AgentToolsService";

interface State {
  loading: boolean;
  updating: boolean;
  adding: boolean;
  club: Club;
  groups: List<Group>;
  menuGroupId: string | null;
  deleteGroup: Group | null;
  defaultGroup: Group | null;
  deleting: boolean;
}

interface Props {
  club: Club;
}
export default class GroupListController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      updating: false,
      adding: false,
      loading: true,
      club: new Club({}),
      groups: new List<Group>(),
      menuGroupId: null,
      deleteGroup: null,
      defaultGroup: null,
      deleting: false,
    };
  }

  override async initialize(props: Props) {
    this.registerAgentResources();
    await this.changeProps(props);
  }

  override async changeProps(newProps: Props): Promise<void> {
    this.state.loading = true;
    this.state.club = newProps.club;
    await this.loadGroups();
  }

  @withStateToggle("loading")
  async loadGroups() {
    const query = this.state.club.groups.query.preloadCounts("players").orderBy("index");
    const groups = await query.all();
    this.state.groups = new List(...groups);
    this.state.groups.orderBy("index");
    this.addDependency(await subscribeList(this.state.groups, query));
  }

  override destroy(): void {
    Group.saveBatch(this.state.groups);
  }

  async saveGroups() {
    this.state.updating = true;
    await batch(async () => {
      await Group.saveBatch(this.state.groups);
      this.state.updating = false;
    });
  }

  async actionAddGroup() {
    this.state.adding = true;

    const index = this.state.groups.length;
    let nameIndex = index;
    let name: string;

    do {
      name = `New group ${++nameIndex}`;
    } while (this.state.groups.some((group) => group.name === name));

    const group = new Group({
      club_id: this.state.club.id,
      name,
      index,
    });

    await group.save();

    this.state.adding = false;
  }

  async actionDeleteGroup(id: string) {
    const group = this.state.groups.findById(id);
    if (!group) return;
    await group.players.loadCount();
    this.state.defaultGroup = this.state.groups.find((g) => g.id !== group.id) ?? null;
    this.state.deleteGroup = group;
  }

  @withStateToggle("deleting")
  async actionConfirmDeleteGroup() {
    try {
      const group = this.state.deleteGroup;

      if (!group) return;
      if (!this.state.defaultGroup) return;

      await ClubPlayer.query().eq("group_id", group.id).updateAll({ group_id: this.state.defaultGroup.id });
      await group.destroy();
    } catch (e) {
      captureException(e);
    } finally {
      this.state.deleteGroup = null;
    }

    // Reload groups after deletion to get the new player counts
    this.loadGroups();
  }

  actionCancelDeleteGroup() {
    this.state.deleteGroup = null;
  }

  async actionSwapGroups(id1: string, id2: string) {
    if (id1 === id2) {
      return;
    }

    const { groups } = this.state;

    groups.forEach((group, index) => {
      if (isNil(group.index)) {
        group.index = generateMidIndex(groups, index - 1, index + 1);
      }
    });

    const sourceIndex = groups.findIndexById(id1);
    const targetIndex = groups.findIndexById(id2);

    if (sourceIndex < targetIndex) {
      groups[sourceIndex].index = generateMidIndex(groups, targetIndex, targetIndex + 1);
    } else {
      groups[sourceIndex].index = generateMidIndex(groups, targetIndex - 1, targetIndex);
    }

    groups.resort();
    await this.saveGroups();
  }

  async actionToggleExcludeGroup(groupId: string, excludeGroupId: string, toggle: boolean) {
    const group = this.state.groups.findById(groupId);
    if (!group) return;

    if (toggle) {
      group.addExcludeGroup(excludeGroupId);
    } else {
      group.removeExcludeGroup(excludeGroupId);
    }

    await this.actionUpdateGroup(group);
  }

  async actionUpdateGroup(group: Group) {
    await group.save();
  }

  private registerAgentResources() {
    this.addDependency(
      agentToolsService.addResources(this, {
        context: () =>
          `The user is viewing and managing club groups. 
           When adding a group specify after as the id of the group to add the new group after or do not specify to add to the start of the list. 
           When adding or updating a group specify the color as an rgb hex value.`,
        definitions: {
          groups: sharedAgentResources(() => ({ club: this.state.club, groups: this.state.groups })).groups,
        },
      }),
    );
  }
}
